import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  StickyNote,
  Plus,
  Share2,
  User,
  Calendar,
  Tag,
  Search,
  Edit,
  Trash2,
  Users,
  ChevronDown,
  Loader
} from 'lucide-react';
import { toast } from 'react-toastify';
import CreateNoteModal from '../../components/modals/CreateNoteModal';
import ShareNoteModal from '../../components/modals/ShareNoteModal';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import useConfirm from '../../hooks/useConfirm';
import Pagination from '../../components/Pagination';
import { handleApiError, notesAPI } from '../../utils/api';
import { useLanguage } from '../../contexts/LanguageContext';

const parseTags = (rawTags = '') => {
  if (!rawTags) return [];
  return rawTags
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);
};

const NOTE_THEMES = [
  { surface: '#FFF7D6', border: '#FCD34D', accent: '#F59E0B', fold: '#FDE68A', pin: '#D97706' },
  { surface: '#E8F3FF', border: '#93C5FD', accent: '#2563EB', fold: '#BFDBFE', pin: '#1D4ED8' },
  { surface: '#ECFDF3', border: '#86EFAC', accent: '#16A34A', fold: '#BBF7D0', pin: '#15803D' },
  { surface: '#FEF2F2', border: '#FCA5A5', accent: '#DC2626', fold: '#FECACA', pin: '#B91C1C' },
  { surface: '#F5F3FF', border: '#C4B5FD', accent: '#7C3AED', fold: '#DDD6FE', pin: '#6D28D9' }
];

const getSeedValue = (value, fallback = 0) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const parsed = Number.parseInt(value, 10);
  if (Number.isFinite(parsed)) return parsed;
  if (typeof value === 'string') {
    return value.split('').reduce((acc, char) => acc + char.charCodeAt(0), fallback);
  }
  return fallback;
};

const NotesPage = () => {
  const { t, currentLanguage } = useLanguage();
  const { confirm, confirmProps } = useConfirm();

  const [notes, setNotes] = useState([]);
  const [stats, setStats] = useState({ own: 0, shared: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(12);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    hasMore: false
  });

  const loadNotes = useCallback(
    async (targetPage = page, search = debouncedSearch, type = filterType) => {
      try {
        setLoading(true);

        const params = {
          page: targetPage,
          limit,
          type,
          t: Date.now()
        };

        if (search) {
          params.search = search;
        }

        const response = await notesAPI.getAll(params);
        const data = response.data || {};
        const payloadNotes = data.notes || [];
        const payloadStats = data.stats || { own: 0, shared: 0, total: 0 };

        setNotes(payloadNotes);
        setStats(payloadStats);

        if (data.pagination) {
          setPagination({
            page: data.pagination.page || targetPage,
            limit: data.pagination.limit || limit,
            total: data.pagination.total ?? payloadStats.total ?? payloadNotes.length,
            hasMore: Boolean(data.pagination.hasMore)
          });
        } else {
          setPagination({
            page: targetPage,
            limit,
            total: payloadStats.total ?? payloadNotes.length,
            hasMore: false
          });
        }

        setPage(targetPage);
      } catch (error) {
        toast.error(handleApiError(error, t('notes.messages.loadError')));
      } finally {
        setLoading(false);
      }
    },
    [debouncedSearch, filterType, limit, page, t]
  );

  const loadTeamMembers = useCallback(async () => {
    try {
      const response = await notesAPI.getTeamMembers();
      const data = response.data || {};
      setTeamMembers(data.team_members || []);
    } catch (error) {
      // silent: sharing modal handles empty members state
    }
  }, []);

  useEffect(() => {
    loadNotes(1);
    loadTeamMembers();
  }, [loadNotes, loadTeamMembers]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    loadNotes(1);
  }, [debouncedSearch, filterType, loadNotes]);

  const formatDate = useCallback(
    (dateStr) => {
      const date = new Date(dateStr);
      const locale =
        currentLanguage === 'fr'
          ? 'fr-FR'
          : currentLanguage === 'es'
            ? 'es-ES'
            : 'en-US';

      return date.toLocaleString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    [currentLanguage]
  );

  const truncateText = (text, maxLength = 180) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return `${text.slice(0, maxLength)}...`;
  };

  const handleDeleteNote = async (noteId) => {
    const result = await confirm({
      title: t('notes.delete.title'),
      message: t('notes.delete.message'),
      type: 'danger',
      confirmText: t('notes.delete.confirmText'),
      cancelText: t('notes.delete.cancelText')
    });

    if (!result) return;

    try {
      await notesAPI.delete(noteId);
      toast.success(t('notes.messages.deleteSuccess'));
      await loadNotes(1);
    } catch (error) {
      toast.error(handleApiError(error, t('notes.messages.deleteErrorGeneral')));
    }
  };

  const handleShareNote = (note) => {
    setSelectedNote(note);
    setShowShareModal(true);
  };

  const resultLabel = useMemo(() => {
    if (pagination.total > 1) {
      return t('notes.results.countPlural').replace('{{count}}', pagination.total);
    }
    return t('notes.results.count').replace('{{count}}', pagination.total);
  }, [pagination.total, t]);

  const isEmptyResult = notes.length === 0;
  const hasSearchOrFilter = Boolean(searchTerm) || filterType !== 'all';

  return (
    <div className="p-6 space-y-6">
      <section className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
              <StickyNote className="w-6 h-6 text-indigo-600" />
              {t('notes.title')}
            </h1>
            <p className="mt-1 text-sm text-gray-600">{t('notes.subtitle')}</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-700"
          >
            <Plus className="w-4 h-4" />
            {t('notes.actions.createNote')}
          </button>
        </div>
      </section>

      <section className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="p-3 rounded-lg bg-gray-50">
            <div className="text-xs text-gray-500">{t('notes.stats.myNotes')}</div>
            <div className="text-lg font-semibold text-gray-900 mt-1">{stats.own}</div>
          </div>
          <div className="p-3 rounded-lg bg-gray-50">
            <div className="text-xs text-gray-500">{t('notes.stats.sharedNotes')}</div>
            <div className="text-lg font-semibold text-gray-900 mt-1">{stats.shared}</div>
          </div>
          <div className="p-3 rounded-lg bg-gray-50">
            <div className="text-xs text-gray-500">{t('notes.stats.total')}</div>
            <div className="text-lg font-semibold text-gray-900 mt-1">{stats.total}</div>
          </div>
        </div>
      </section>

      <section className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex flex-col md:flex-row gap-3 md:items-center">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder={t('notes.search.placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full md:w-80 border border-gray-200 rounded-lg px-9 py-2 text-sm"
              />
            </div>
            <div className="relative">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 pr-8 py-2 text-sm appearance-none"
              >
                <option value="all">{t('notes.filters.all')}</option>
                <option value="own">{t('notes.filters.own')}</option>
                <option value="shared">{t('notes.filters.shared')}</option>
              </select>
              <ChevronDown className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
          <div className="text-xs text-gray-500">{resultLabel}</div>
        </div>
      </section>

      {loading ? (
        <div className="bg-white rounded-lg border border-gray-200 p-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center justify-center w-full text-sm text-gray-500">
              <Loader className="w-4 h-4 animate-spin mr-2" />
              {t('common.loading') || 'Loading...'}
            </div>
          </div>
        </div>
      ) : isEmptyResult ? (
        <section className="bg-white rounded-lg border border-gray-200 p-8 text-center">
          <StickyNote className="w-10 h-10 mx-auto text-gray-300" />
          <h3 className="mt-3 text-sm font-semibold text-gray-900">
            {hasSearchOrFilter ? t('notes.empty.noNotesFound') : t('notes.empty.noNotesYet')}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {hasSearchOrFilter ? t('notes.empty.tryModifyingSearch') : t('notes.empty.createFirstNote')}
          </p>
          {!hasSearchOrFilter && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-700"
            >
              <Plus className="w-4 h-4" />
              {t('notes.empty.createNote')}
            </button>
          )}
        </section>
      ) : (
        <section className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {notes.map((note, index) => {
              const tags = parseTags(note.tags);
              const isOwn = note.note_type === 'own';
              const seed = getSeedValue(note.id, index + 1);
              const theme = NOTE_THEMES[seed % NOTE_THEMES.length];
              const rotation = ((seed % 5) - 2) * 0.7;

              return (
                <motion.article
                  key={note.id}
                  initial={{ opacity: 0, y: 12, rotate: rotation }}
                  animate={{ opacity: 1, y: 0, rotate: rotation }}
                  whileHover={{ y: -4, rotate: 0 }}
                  transition={{ duration: 0.2 }}
                  className="relative min-h-[280px] rounded-2xl border px-4 py-5 flex flex-col gap-4 overflow-hidden"
                  style={{
                    backgroundColor: theme.surface,
                    borderColor: theme.border,
                    boxShadow: '0 8px 18px rgba(15, 23, 42, 0.08)'
                  }}
                >
                  <div className="absolute top-0 left-0 h-1.5 w-full" style={{ backgroundColor: theme.accent }} />
                  <div
                    className="absolute top-0 right-0 h-0 w-0 border-l-[26px] border-b-[26px]"
                    style={{ borderLeftColor: 'transparent', borderBottomColor: theme.fold }}
                  />
                  <div
                    className="absolute top-3 right-3 h-2.5 w-2.5 rounded-full ring-2 ring-white/70"
                    style={{ backgroundColor: theme.pin }}
                  />

                  <header className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate text-base font-semibold text-gray-900">{note.title}</h3>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-600">
                        <span className="inline-flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {note.author_name}
                        </span>
                        {note.note_type === 'shared' && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-white/70 px-2 py-0.5 text-indigo-700 border border-indigo-200">
                            <Share2 className="w-3 h-3" />
                            {note.shared_by_name
                              ? t('notes.card.sharedBy').replace('{{name}}', note.shared_by_name)
                              : t('notes.card.shared')}
                          </span>
                        )}
                      </div>
                    </div>

                    {isOwn && (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleShareNote(note)}
                          className="rounded-lg p-2 text-gray-500 transition hover:bg-indigo-50 hover:text-indigo-700"
                          title={t('notes.card.tooltips.share')}
                          aria-label={t('notes.card.tooltips.share')}
                        >
                          <Share2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedNote(note);
                            setShowCreateModal(true);
                          }}
                          className="rounded-lg p-2 text-gray-500 transition hover:bg-indigo-50 hover:text-indigo-700"
                          title={t('notes.card.tooltips.edit')}
                          aria-label={t('notes.card.tooltips.edit')}
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteNote(note.id)}
                          className="rounded-lg p-2 text-gray-500 transition hover:bg-red-50 hover:text-red-700"
                          title={t('notes.card.tooltips.delete')}
                          aria-label={t('notes.card.tooltips.delete')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </header>

                  <p className="text-sm leading-relaxed text-gray-700 whitespace-pre-line break-words flex-1">
                    {truncateText(note.content, 260)}
                  </p>

                  {tags.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2">
                      {tags.slice(0, 5).map((tag) => (
                        <span
                          key={`${note.id}-${tag}`}
                          className="inline-flex items-center gap-1 rounded-full border border-gray-300/80 bg-white/70 px-2 py-0.5 text-[11px] font-medium text-gray-700"
                        >
                          <Tag className="w-3 h-3" />
                          {tag}
                        </span>
                      ))}
                      {tags.length > 5 && (
                        <span className="text-[11px] text-gray-600">+{tags.length - 5}</span>
                      )}
                    </div>
                  )}

                  <footer className="flex items-center justify-between border-t border-black/5 pt-3 text-xs text-gray-600">
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(note.updated_at || note.created_at)}
                    </span>
                    {note.is_shared && (
                      <span className="inline-flex items-center gap-1 text-indigo-700">
                        <Users className="w-3 h-3" />
                        {t('notes.card.shared')}
                      </span>
                    )}
                  </footer>
                </motion.article>
              );
            })}
          </div>
        </section>
      )}

      <Pagination
        currentPage={pagination.page}
        totalItems={pagination.total}
        itemsPerPage={pagination.limit}
        onPageChange={(nextPage) => loadNotes(nextPage)}
        className="mt-1"
      />

      <CreateNoteModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setSelectedNote(null);
        }}
        note={selectedNote}
        onSuccess={async () => {
          await loadNotes(1);
        }}
      />

      <ShareNoteModal
        isOpen={showShareModal}
        onClose={() => {
          setShowShareModal(false);
          setSelectedNote(null);
        }}
        note={selectedNote}
        teamMembers={teamMembers}
        onSuccess={async () => {
          await loadNotes(1);
        }}
      />

      <ConfirmationModal {...confirmProps} />
    </div>
  );
};

export default NotesPage;
